import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Target } from 'lucide-react';

const ThreeStepRecipe = () => {
  // Define a common animation for the icons
  const iconAnimation = {
    rotateY: 360, // Rotate around the Y-axis for a left-to-right roll
  };

  // Define a common transition for the icons
  const iconTransition = {
    duration: 10, // 10 seconds for one full rotation (slow)
    ease: "linear", // Constant speed
    repeat: Infinity, // Loop indefinitely
  };

  // Define a set of soft, glowing pastel colors for text animation
  const textColors = [
    "#E0BBE4", // Soft Lavender
    "#957DAD", // Muted Purple
    "#D291BC", // Dusty Rose
    "#FFC72C", // Soft Gold (similar to your badge)
    "#A7DBD8", // Pale Teal
    "#F7CAC9", // Light Pink
  ];

  // Define text animation properties
  const textAnimation = {
    color: textColors, // Animate through these colors
  };

  const textTransition = {
    duration: 20, // Very slow transition over 20 seconds
    ease: "linear", // Smooth, continuous change
    repeat: Infinity, // Loop indefinitely
    repeatType: "mirror", // Go back and forth through the colors
  };

  // Define colors for the flickering light effect
  const flickerColors = [
    "rgba(0, 0, 255, 0.2)", // Blue
    "rgba(255, 0, 0, 0.2)", // Blood Red
  ];

  // Define flicker animation properties
  const flickerAnimation = {
    background: [
      `radial-gradient(circle at center, ${flickerColors[0]} 0%, rgba(0, 0, 255, 0) 70%)`,
      `radial-gradient(circle at center, ${flickerColors[1]} 0%, rgba(255, 0, 0, 0) 70%)`,
      `radial-gradient(circle at center, ${flickerColors[0]} 0%, rgba(0, 0, 255, 0) 70%)`,
    ],
    opacity: [0.1, 0.3, 0.2, 0.4, 0.1], // Varying opacity for flicker
    scale: [1, 1.05, 0.95, 1.02, 1], // Subtle scale for breathing effect
    x: ['-5%', '5%', '-2%', '3%', '0%'], // Slight horizontal shift
    y: ['-5%', '3%', '-4%', '2%', '0%'], // Slight vertical shift
  };

  const flickerTransition = {
    duration: 30, // Longer duration for color change, ensuring no sync with text (20s)
    ease: "easeInOut",
    repeat: Infinity,
    repeatType: "mirror",
  };

  // Define heading animation properties
  const headingAnimation = {
    scale: [1, 1.02, 1], // Scale up slightly and back down
  };

  const headingTransition = {
    duration: 5, // Slow transition over 5 seconds
    ease: "easeInOut", // Smooth ease
    repeat: Infinity, // Loop indefinitely
    repeatType: "mirror", // Go back and forth
  };

  const steps = [
    {
      icon: Sparkles,
      title: "Clean Up Your Credit",
      description: "Identify and dispute inaccuracies, remove negative items, and set the foundation for a healthier financial profile."
    },
    {
      icon: TrendingUp,
      title: "Boost Your Scores",
      description: "Implement proven strategies to rapidly increase your credit scores, opening doors to better rates and opportunities."
    },
    {
      icon: Target,
      title: "Crush Your Debt",
      description: "Develop a personalized plan to tackle and eliminate debt efficiently, freeing up your finances for what truly matters."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.5 }}
      className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12 text-center"
    >
      <motion.h2
        className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 mb-8"
        animate={headingAnimation} // Apply heading animation
        transition={headingTransition} // Apply heading transition
      >
        Our 3-Step Recipe for Success
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 + index * 0.2 }}
            className="relative bg-gray-900/50 p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center overflow-hidden" // Added overflow-hidden for contained glow
            style={{
              boxShadow: '0 0 15px rgba(0, 255, 255, 0.6), 0 0 30px rgba(0, 255, 255, 0.4)' // Turquoise glow
            }}
          >
            {/* Flickering Candle Light Effect */}
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ filter: 'blur(20px)', zIndex: -2 }} // Very blurry, behind everything else
              animate={flickerAnimation}
              transition={flickerTransition}
            />

            {/* Existing Neon Glow Effect for each step card */}
            <motion.div
              className="absolute inset-0 rounded-2xl" // No negative margin, just inset
              style={{
                background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', // Pearl white gradient
                filter: 'blur(8px)', // Soft glow
                zIndex: -1, // Behind the main content
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', // Mask to create border effect
                WebkitMaskComposite: 'xor', // Combine masks
                maskComposite: 'exclude', // Standard for exclude
              }}
              animate={{ opacity: [0.2, 0.5, 0.2] }} // Slow flicker
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="p-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mb-4"
              animate={iconAnimation} // Apply the rotation animation
              transition={iconTransition} // Apply the rotation transition
            >
              <step.icon className="text-white" size={36} />
            </motion.div>
            <motion.h3
              className="text-xl font-bold mb-3"
              animate={textAnimation} // Apply color animation
              transition={textTransition} // Apply color transition
            >
              {step.title}
            </motion.h3>
            <motion.p
              className="text-gray-300" // Keep base gray for description, but animate color
              animate={textAnimation} // Apply color animation
              transition={textTransition} // Apply color transition
            >
              {step.description}
            </motion.p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ThreeStepRecipe;